import React, { useState, useEffect } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Autocomplete, Chip,
} from '@mui/material';

type ColumnType = 'text' | 'number';

interface ColumnDefinition {
  name: string;
  type: ColumnType;
}

interface UniqueOption {
  value: string;
  isNew: boolean;
}

interface Row {
  [key: string]: string | number | null;
}

export interface UniqueValues {
  [key: string]: UniqueOption[];
}

interface EditDataTableProps {
  columns: ColumnDefinition[];
  initialRows: Row[];
  uniqueValues?: UniqueValues;
  onDataChange?: (rows: Row[], isComplete: boolean) => void;
}

export const EditDataTable: React.FC<EditDataTableProps> = ({
  columns,
  initialRows,
  uniqueValues,
  onDataChange,
}) => {
  const [rows, setRows] = useState<Row[]>(initialRows);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [internalUniqueValues, setInternalUniqueValues] = useState<UniqueValues>({});

  // Initialize internal unique values
  useEffect(() => {
    if (uniqueValues) {
      const formattedValues: UniqueValues = {};
      Object.entries(uniqueValues).forEach(([key, values]) => {
        formattedValues[key] = values.map((value) => ({
          value: value?.value?.trim(),
          isNew: false
        })).sort((a, b) => a.value?.localeCompare(b.value));
      });
      setInternalUniqueValues(formattedValues);
    }
  }, [uniqueValues]);

  // Check if all cells are filled
  useEffect(() => {
    const complete = rows.every((row) =>
      columns.every(
        (column) =>
          row[column.name] !== null && row[column.name] !== undefined && row[column.name] !== '',
      ),
    );

    setIsComplete(complete);

    if (onDataChange) {
      onDataChange(rows, complete);
    }
  }, [rows, columns, onDataChange]);

  const handleCellChange = (rowIndex: number, columnName: string, value: string | null) => {
    const newRows = [...rows];
    const trimmedValue = value?.trim() ?? null;

    // Convert to number if the column type is number and the value is valid
    if (columns.find((col) => col.name === columnName)?.type === 'number') {
      const numValue = trimmedValue === '' || trimmedValue === null ? null : Number(trimmedValue);
      // Only update if it's a valid number or empty string
      if (trimmedValue === '' || trimmedValue === null || !isNaN(numValue as number)) {
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          [columnName]: trimmedValue === '' || trimmedValue === null ? null : numValue,
        };
      }
    } else {
      // For text fields
      newRows[rowIndex] = {
        ...newRows[rowIndex],
        [columnName]: trimmedValue,
      };
    }

    setRows(newRows);
  };

  const handleBlur = (columnName: string, value: string | null) => {
    if (!value || !internalUniqueValues[columnName]) {return;}

    const trimmedValue = value.trim();
    const exists = internalUniqueValues[columnName].some(
      (option) => option.value === trimmedValue
    );

    if (!exists) {
      const newValues = [...internalUniqueValues[columnName]];
      newValues.push({ value: trimmedValue, isNew: true });
      newValues.sort((a, b) => a.value.localeCompare(b.value));

      setInternalUniqueValues({
        ...internalUniqueValues,
        [columnName]: newValues
      });
    }
  };

  // Determine if a cell needs to be filled (is empty)
  const isCellEmpty = (row: Row, columnName: string): boolean => {
    return row[columnName] === null || row[columnName] === undefined || row[columnName] === '';
  };

  return (
    <Box
      sx={{
        mb: 4,
        border: '1px solid',
        borderColor: isComplete ? 'success.main' : 'grey.300',
        borderRadius: 1,
        transition: 'border-color 0.3s ease',
      }}
    >
      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
        <Table size="small" aria-label="editable data table">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.light' }}>
              {columns.map((column) => (
                <TableCell key={column.name} sx={{ fontWeight: 'bold' }}>
                  {column.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={`${rowIndex}-${column.name}`}
                    sx={{
                      backgroundColor: isCellEmpty(row, column.name)
                        ? 'rgba(255, 235, 235, 0.3)'
                        : 'inherit',
                    }}
                  >
                    {internalUniqueValues[column.name] ? (
                      <Autocomplete
                        freeSolo
                        fullWidth
                        size="small"
                        options={internalUniqueValues[column.name]}
                        value={row[column.name]?.toString() || ''}
                        onChange={(_, newValue) => {
                          const value = typeof newValue === 'string' ? newValue : newValue?.value;
                          handleCellChange(rowIndex, column.name, value || null);
                        }}
                        onInputChange={(_, newValue) => handleCellChange(rowIndex, column.name, newValue)}
                        onBlur={(e) => {
                          const target = e.target as HTMLInputElement;
                          handleBlur(column.name, target.value);
                        }}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') {return option;}
                          return option.value;
                        }}
                        renderOption={(props, option) => {
                          // Extract the key from props
                          const { key, ...otherProps } = props;

                          return (
                            <li key={key} {...otherProps}>
                              {typeof option === 'string' ? option : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {option.value}
                                  {option.isNew && (
                                    <Chip
                                      label="New"
                                      size="small"
                                      color="primary"
                                      sx={{ height: 20 }}
                                    />
                                  )}
                                </Box>
                              )}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            error={isCellEmpty(row, column.name)}
                            placeholder={isCellEmpty(row, column.name) ? 'Required' : ''}
                            InputProps={{
                              ...params.InputProps,
                              disableUnderline: !isCellEmpty(row, column.name),
                            }}
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        variant="standard"
                        value={
                          row[column.name] !== null && row[column.name] !== undefined
                            ? row[column.name]
                            : ''
                        }
                        onChange={(e) => handleCellChange(rowIndex, column.name, e.target.value)}
                        type={column.type === 'number' ? 'number' : 'text'}
                        size="small"
                        error={isCellEmpty(row, column.name)}
                        placeholder={isCellEmpty(row, column.name) ? 'Required' : ''}
                        InputProps={{
                          disableUnderline: !isCellEmpty(row, column.name),
                        }}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default EditDataTable;
